// One receipt-based wait spans thinking and the prepared reply preview.
import '@apps/web/components/shared/messages/automated_reply_status.css';

import { Clock, Pause, Play, SkipForward } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Spinner } from '@apps/web/components/ui/spinner';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import { type OrganizationConversation } from '@libraries/typescript/api_client';

type AutomatedReplyStatusProps = {
    readonly conversation: OrganizationConversation;
    readonly organizationId: string | null;
    readonly showControls: boolean;
    readonly onUpdate: () => Promise<unknown>;
};

export function AutomatedReplyStatus({
    conversation,
    organizationId,
    showControls,
    onUpdate,
}: AutomatedReplyStatusProps) {
    const [now, setNow] = useState(Date.now);
    const [action, setAction] = useState<'pause' | 'resume' | 'skip' | null>(
        null,
    );
    const prepared = Boolean(conversation.automated_reply_send_at);
    const processing =
        conversation.processing_state === 'debouncing' ||
        conversation.processing_state === 'processing';
    const thinking =
        processing && Boolean(conversation.automated_reply_wait_until);
    const pauseUntil = conversation.automated_reply_paused_until
        ? new Date(conversation.automated_reply_paused_until).getTime()
        : 0;
    const paused = pauseUntil > now;
    const visible =
        prepared ||
        thinking ||
        (conversation.processing_state === 'debouncing' && paused);
    const waitUntil =
        conversation.automated_reply_wait_until ??
        conversation.automated_reply_send_at;
    const remaining = waitUntil
        ? Math.max(0, Math.ceil((new Date(waitUntil).getTime() - now) / 1000))
        : 0;
    const preview = conversation.automated_reply_preview ?? [];
    const sourceMessageId = conversation.automated_reply_source_message_id;

    useEffect(() => {
        if (!visible) {
            return undefined;
        }
        setNow(Date.now());
        const interval = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(interval);
    }, [visible, waitUntil, pauseUntil]);

    async function updateReply(nextAction: 'pause' | 'resume' | 'skip') {
        if (!organizationId || action !== null) {
            return;
        }
        setAction(nextAction);
        const pathParams = {
            organization_id: organizationId,
            conversation_id: conversation.id,
        };
        try {
            if (nextAction === 'skip') {
                if (!sourceMessageId) {
                    return;
                }
                await getApiClient().patch({
                    url: '/organizations/{organization_id}/conversations/{conversation_id}/automated-reply-skip',
                    pathParams,
                    body: { source_message_id: sourceMessageId },
                });
            } else {
                await getApiClient().patch({
                    url: '/organizations/{organization_id}/conversations/{conversation_id}/automated-reply-pause',
                    pathParams,
                    body: {
                        paused: nextAction === 'pause',
                        source_message_id: sourceMessageId ?? null,
                    },
                });
            }
            await onUpdate();
            if (nextAction !== 'skip') {
                toast.success(
                    nextAction === 'pause'
                        ? 'Phoebe paused for 15 minutes'
                        : 'Phoebe resumed',
                    { persistNotification: false },
                );
            }
        } catch (error) {
            console.error('Failed to update Phoebe reply:', error);
            toast.error('Couldn’t update this reply. Please try again.', {
                persistNotification: false,
            });
            // A newer incoming message may have replaced the clicked turn.
            await onUpdate();
        } finally {
            setAction(null);
        }
    }

    if (!visible || (!prepared && conversation.pending_messages?.length)) {
        return null;
    }

    const label = paused
        ? `Phoebe paused · ${Math.ceil((pauseUntil - now) / 60_000)}m left`
        : prepared
          ? remaining > 0
              ? `Phoebe replying in ${remaining}s`
              : 'Sending reply…'
          : 'Phoebe is thinking';

    return (
        <div className="@container/reply-status flex justify-center py-2">
            <section
                className={cn(
                    // Narrow panes need room below the reply for the floating launcher.
                    'automated-reply-status border-border/70 bg-muted/40 text-muted-foreground mb-16 min-w-0 rounded-lg border text-xs @[50rem]/reply-status:mb-0',
                    prepared && preview.length > 0
                        ? 'w-full max-w-[680px]'
                        : '',
                )}
                aria-label="Phoebe reply status"
                data-testid="conversation-processing-status"
            >
                <div className="flex min-h-10 flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                        {paused ? (
                            <Pause
                                className="size-3.5 shrink-0"
                                aria-hidden="true"
                            />
                        ) : prepared ? (
                            <Clock
                                className="size-3.5 shrink-0"
                                aria-hidden="true"
                            />
                        ) : (
                            <Spinner
                                size="sm"
                                className="motion-reduce:animate-none"
                            />
                        )}
                        <span
                            key={
                                paused
                                    ? 'paused'
                                    : prepared
                                      ? 'prepared'
                                      : 'thinking'
                            }
                            className="automated-reply-label font-medium tabular-nums"
                        >
                            {label}
                        </span>
                        {!paused && !prepared && remaining > 0 && (
                            <span className="text-muted-foreground/75 whitespace-nowrap tabular-nums">
                                Wait · {remaining}s
                            </span>
                        )}
                    </div>
                    {showControls && organizationId && (
                        <div className="flex shrink-0 items-center gap-1">
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-7 gap-1.5 px-2 text-xs duration-120 motion-reduce:transition-none"
                                data-testid="conversation-automated-reply-pause-toggle"
                                disabled={action !== null}
                                onClick={() =>
                                    void updateReply(
                                        paused ? 'resume' : 'pause',
                                    )
                                }
                            >
                                {action === 'pause' || action === 'resume' ? (
                                    <Spinner
                                        size="xs"
                                        className="motion-reduce:animate-none"
                                    />
                                ) : paused ? (
                                    <Play className="size-3" />
                                ) : (
                                    <Pause className="size-3" />
                                )}
                                {paused ? 'Resume' : 'Pause'}
                            </Button>
                            {sourceMessageId && !paused && (
                                <Tooltip>
                                    <TooltipTrigger asChild={true}>
                                        <Button
                                            type="button"
                                            size="icon"
                                            variant="ghost"
                                            className="size-7 duration-120 motion-reduce:transition-none"
                                            aria-label="Skip reply wait"
                                            data-testid="conversation-automated-reply-skip"
                                            disabled={
                                                action !== null ||
                                                remaining === 0
                                            }
                                            onClick={() =>
                                                void updateReply('skip')
                                            }
                                        >
                                            {action === 'skip' ? (
                                                <Spinner
                                                    size="xs"
                                                    className="motion-reduce:animate-none"
                                                />
                                            ) : (
                                                <SkipForward className="size-3.5" />
                                            )}
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className="motion-reduce:animate-none">
                                        {prepared
                                            ? 'Send now'
                                            : 'Skip wait · send when ready'}
                                    </TooltipContent>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
                {prepared && preview.length > 0 && (
                    <div
                        className="automated-reply-preview border-border/70 bg-background/60 divide-border/60 divide-y border-t"
                        data-testid="conversation-automated-reply-preview"
                        aria-label="Reply preview"
                    >
                        {preview.map((part) => (
                            <p
                                key={part.id}
                                className="text-foreground px-4 py-3 text-sm leading-relaxed break-words whitespace-pre-wrap"
                            >
                                {part.content}
                            </p>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
